import type { FC } from "react";
import { useMemo } from "react";

import { Container, ContentContainer } from "@src/core/styles";
import { useSeo } from "@src/core/hooks";
import { APP_ROUTES } from "@src/core/constants";
import {
  ArticleBody,
  Section,
  SectionTitle,
  ToolContainer,
  ToolIntro,
  ToolSubtitle,
  ToolTitle,
} from "@src/pages/dev-tools-shared";
import {
  CategoryLabel,
  ToolCard,
  ToolCardDesc,
  ToolCardTitle,
  ToolsGrid,
} from "./tools-hub-page.styles";

interface ToolEntry {
  title: string;
  description: string;
  path: string;
  category: string;
}

const TOOLS: ToolEntry[] = [
  {
    title: "JSON Beautifier & Validator",
    description:
      "Форматирование, минификация и проверка синтаксиса JSON с подсветкой ошибок.",
    path: APP_ROUTES.JSON_VALIDATOR,
    category: "JSON",
  },
  {
    title: "JWT Debugger",
    description:
      "Декодирование header/payload и проверка подписи HS256/384/512 без отправки токена на сервер.",
    path: APP_ROUTES.JWT_DEBUGGER,
    category: "Безопасность",
  },
  {
    title: "Base64 Encoder / Decoder",
    description:
      "Кодирование и декодирование Base64, URL‑safe режим, поддержка UTF‑8 и файлов.",
    path: APP_ROUTES.BASE64,
    category: "Кодировки",
  },
  {
    title: "URL Encoder / Decoder",
    description:
      "encodeURIComponent vs encodeURI, разбор URL на части и парсинг query‑параметров.",
    path: APP_ROUTES.URL_ENCODER,
    category: "URL",
  },
  {
    title: "UUID Generator",
    description:
      "Массовая генерация UUID v1 / v4 / v7, копирование и валидация формата.",
    path: APP_ROUTES.UUID_GENERATOR,
    category: "Идентификаторы",
  },
];

export const ToolsHubPage: FC = () => {
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Инструменты для разработчиков",
      itemListElement: TOOLS.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: t.path,
        name: t.title,
      })),
    }),
    []
  );

  useSeo({
    title: "Инструменты для разработчиков — JWT, Base64, URL, UUID, JSON",
    description:
      "Бесплатные онлайн‑утилиты для разработчиков и QA: JWT debugger, Base64, URL encoder, UUID generator и JSON validator. Всё работает в браузере.",
    canonical: APP_ROUTES.TOOLS,
    jsonLd,
  });

  return (
    <Container>
      <ContentContainer>
        <ToolContainer>
          <ToolIntro>
            <ToolTitle>Инструменты для разработчиков</ToolTitle>
            <ToolSubtitle>
              Коллекция бесплатных онлайн‑утилит, которые ежедневно нужны
              бэкендерам, фронтендерам и QA. Все вычисления происходят в вашем
              браузере — чувствительные данные никуда не уходят.
            </ToolSubtitle>
          </ToolIntro>

          <ToolsGrid>
            {TOOLS.map((tool) => (
              <ToolCard key={tool.path} to={tool.path}>
                <CategoryLabel>{tool.category}</CategoryLabel>
                <ToolCardTitle>{tool.title}</ToolCardTitle>
                <ToolCardDesc>{tool.description}</ToolCardDesc>
              </ToolCard>
            ))}
          </ToolsGrid>

          <Section>
            <SectionTitle>Чего не хватает?</SectionTitle>
            <ArticleBody>
              <p>
                Мы развиваем набор инструментов в том же контексте, что и
                основной продукт LightBoxAPI — сервис{" "}
                <a href={APP_ROUTES.DASHBOARD}>мокирования API</a>. На подходе:
                JSON → TypeScript / Go, конвертер cURL в готовый мок‑маршрут,
                генератор тестовых данных и webhook inspector.
              </p>
              <p>
                Не хватает какого‑то инструмента? Напишите нам через форму{" "}
                <a href={APP_ROUTES.HELP}>обратной связи</a>.
              </p>
            </ArticleBody>
          </Section>
        </ToolContainer>
      </ContentContainer>
    </Container>
  );
};
